import { Badge, BadgeProps, Box, Text } from "@mantine/core";
import classes from "./status-badge.module.css";

interface Props extends Omit<BadgeProps, "color" | "children"> {
  label?: string | null;
  color?: string | null;
  fallback?: string;
  uppercase?: boolean;
  withDot?: boolean;
}

/**
 * DeHUB UI стандарт: төлөвийн шошго саарал дэвсгэр + цэг + үг (#334155). Өнгө зөвхөн цэгт:
 * ногоон = амжилттай, улаан = алдаа/хугацаа хэтэрсэн, шар = анхаарах, бусад нь саарал.
 */
const DOT_COLORS = new Set(["green", "red", "yellow"]);
const DOT_ALIAS: Record<string, string> = { teal: "green", lime: "green", orange: "yellow" };

const STATUS_COLOR_BY_CODE: Record<string, string> = {
  DRAFT: "gray",
  CANCELED: "gray",
  CANCELLED: "gray",
  CLOSED: "gray",
  RETURNED: "gray",
  CONFIRMED: "gray",
  APPROVED: "gray",
  SENT: "gray",
  PARTIAL: "yellow",
  PAID: "green",
  OVERDUE: "red",
  REJECTED: "red",
  DECLINED: "red",
};

export function resolveInvoiceStatusColor(
  code?: string | null,
  fallbackColor?: string | null,
): string {
  if (code) {
    const mapped = STATUS_COLOR_BY_CODE[code.trim().toUpperCase()];
    if (mapped) return mapped;
  }
  return resolveColor(fallbackColor);
}

/** Цэгийн өнгө: ногоон / улаан / шар, бусад бүх өнгө (ягаан, цэнхэр, нил ягаан ...) саарал. */
function resolveColor(raw?: string | null): string {
  if (!raw) return "gray";
  const base = raw.split(".")[0].trim().toLowerCase();
  const alias = DOT_ALIAS[base] ?? base;
  return DOT_COLORS.has(alias) ? alias : "gray";
}

function StatusDot({ color }: { color: string }) {
  return (
    <Box
      component="span"
      className={classes.dot}
      __vars={{ "--status-dot-color": color === "gray" ? "var(--mantine-color-gray-7)" : `var(--mantine-color-${color}-7)` }}
    />
  );
}

/** Table/list status chip — 26px tall, 14px/600, 6px dot. */
export function StatusBadge({
  label,
  color,
  fallback = "-",
  uppercase = false,
  withDot = true,
  ...rest
}: Props) {
  const display = (label ?? "").trim();
  if (!display) {
    return (
      <Text size="sm" c="dimmed">
        {fallback}
      </Text>
    );
  }
  const resolvedColor = resolveColor(color);
  return (
    <Badge
      className={classes.badge}
      variant="light"
      color="gray"
      tt={uppercase ? "uppercase" : "none"}
      leftSection={withDot ? <StatusDot color={resolvedColor} /> : undefined}
      {...rest}
    >
      {display}
    </Badge>
  );
}
